import flowPlot from "../utils/flowPlot";
import {
  GetTimeseriesResponse,
  TimeseriesDataPoint,
} from "../types/timeseries";

import { SeriesProps } from "../types/echarts";
import useQueryState from "./useQueryState";
import { getAnomalyColor } from "../helpers/anomaliesHelper";
import { CLIMATE_LAYERS } from "../enums/layers";
import { kebabCase, startCase, toLower } from "lodash";

interface UseSeriesProps {
  colors: string[];
  data: GetTimeseriesResponse;
  droughtEventData: TimeseriesDataPoint[];
  ipcScoreData: TimeseriesDataPoint[];
  type?: string;
}

const useSeries = (props: UseSeriesProps) => {
  const { data, droughtEventData, ipcScoreData, type } = props;
  const {
    queryState: { layers, droughtEvents, ipcScore, climate },
  } = useQueryState();

  const series: SeriesProps[] = data
    .map(({ data, displayName, indicator }, i) => {
      return {
        data,
        lineStyle: { width: 1, color: getAnomalyColor(kebabCase(indicator)) },
        itemStyle: { color: getAnomalyColor(kebabCase(indicator)) },
        symbol: "line",
        showSymbol: false as const,
        name: `${startCase(toLower(indicator))} ${type === "climate" ? "" : displayName.includes("Hybrid") ? "Index" : "Anomaly"}`,
        type: displayName?.includes("Anomaly") ? "bar" : "line",
        seriesLayoutBy: "row",
        smooth: true,
      };
    })
    .sort((a: any, b: any) => {
      const A = a.name ?? "";
      const B = b.name ?? "";

      const isHybridA = A === "Hybrid Anomaly";
      const isHybridB = B === "Hybrid Anomaly";

      if (isHybridA && !isHybridB) return 1;
      if (!isHybridA && isHybridB) return -1;

      return A.localeCompare(B);
    });

  if (droughtEvents && droughtEventData) {
    let markedAreaData: { xAxis: string }[][] = [];

    droughtEventData?.forEach((data, index: number) => {
      if (data[1] > 0)
        markedAreaData.push([
          { xAxis: data[0] },
          { xAxis: droughtEventData[index + 1]?.[0] },
        ]);
    });

    const droughtSeries: SeriesProps = {
      name: "Drought Event",
      type: "line",
      symbol: "rect",
      symbolSize: 10,
      itemStyle: {
        color: "#B8EA4E",
      },
      showSymbol: false,
      tooltip: {
        show: false,
      },

      emphasis: {
        showSymbol: false,
      },
      lineStyle: { opacity: 0, width: 0 },
      data: droughtEventData,
      markArea: {
        silent: true,
        emphasis: { disabled: true },
        data: markedAreaData,
        itemStyle: {
          color: "#B8EA4E",
        },
      },
    };

    series.push(droughtSeries);
  }

  if (ipcScore && ipcScoreData) {
    const ipcScoreSeries: SeriesProps = {
      name: "IPC Score",
      type: "scatter",
      symbolSize: 5,
      lineStyle: { opacity: 0, width: 0, color: "red" },
      data: ipcScoreData,
      showSymbol: false,
      yAxisIndex: 1,
      itemStyle: {
        color: "red",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
    };

    series.push(ipcScoreSeries);
  }

  const plot = flowPlot({
    series,
    displayName: layers[0]?.displayName,
    unit:
      type === "climate"
        ? CLIMATE_LAYERS.find((layer) => layer.displayName === climate)
            ?.dataunit
        : "anomaly",
    ipcScore,
    droughtEvents,
    isClimateData: type === "climate",
  });

  return plot;
};

export default useSeries;
